import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, MapPin } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Today-summary card (Feb 2026).
 *
 * Renders the worker's "You worked X.Y hours in {area} today" rollup on
 * the Home page. Calls GET /api/clock-in/today-summary which returns
 * `{ total_hours, by_area:[{area_label, hours, entries}], active_entry }`.
 *
 * Render rules
 * ------------
 * - Hide entirely when total_hours == 0 AND no active entry — avoids a
 *   noisy "0.0 hours" card on freshly-loaded mornings.
 * - When `active_entry` exists we say "so far today" instead of past tense.
 * - When there are multiple areas, list the top 3 — the rest roll into
 *   "+N more" so the card stays compact on mobile.
 */
function fmtHours(h) {
  if (!h) return '0';
  if (h < 0.1) {
    // Show minutes for sub-6-min entries so the card doesn't say "0h"
    const mins = Math.round(h * 60);
    return `${mins}m`;
  }
  return `${h.toFixed(1)}h`;
}

export default function TodaySummaryCard({ token, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/clock-in/today-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [token, refreshKey]);

  if (!loaded || !data) return null;
  const { total_hours: total, by_area: areas = [], active_entry: active } = data;
  // Hide on quiet mornings (no past hours + not currently clocked in)
  if (!total && !active) return null;

  // Filter out zero-hour entries (e.g. the freshly-started active one shows
  // 0h until time passes — covered by the "so far today" wording).
  const meaningful = areas.filter((a) => a.hours > 0 || a.area_label === active?.area_label);
  const top = meaningful.slice(0, 3);
  const remaining = meaningful.length - top.length;

  // Pick the headline area: prefer the busiest, or the active one if all 0h
  const headline = top[0]?.area_label || (active?.area_label) || null;
  const isActive = !!active;

  return (
    <div
      className="ht-card p-4 mb-4"
      style={{ borderLeft: '3px solid #00C896' }}
      data-testid="today-summary-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,200,150,0.15)' }}>
          <Clock className="w-5 h-5" style={{ color: '#00C896' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white" data-testid="today-summary-headline">
            {isActive ? (
              <>You&rsquo;ve worked <span className="text-emerald-400">{fmtHours(total)}</span> so far today</>
            ) : (
              <>You worked <span className="text-emerald-400">{fmtHours(total)}</span> today</>
            )}
            {headline && (
              <span className="text-slate-300"> in <span className="font-mono">{headline}</span></span>
            )}
          </p>
          {top.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="today-summary-areas">
              {top.map((a) => (
                <span
                  key={a.area_label || '__no_area'}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border"
                  style={{
                    background: 'rgba(0,200,150,0.08)',
                    borderColor: 'rgba(0,200,150,0.3)',
                    color: '#A7F3D0',
                  }}
                  data-testid={`today-summary-area-${(a.area_label || 'no-area').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                >
                  <MapPin className="w-3 h-3" /> {a.area_label || 'No area'}
                  <span className="text-slate-300 ml-0.5 font-medium">{fmtHours(a.hours)}</span>
                </span>
              ))}
              {remaining > 0 && (
                <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border border-[#2D3748] text-slate-400">
                  +{remaining} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
